import { DistanceIcon, DurationIcon, Ride, useUnits } from '@geovelo-frontends/commons';
import { Check } from '@mui/icons-material';
import { Box, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import defaultRideImage from '../assets/images/ride.jpg';

import { ArrowOutward } from './icons';

function RideCard({
  fullHeight,
  outlined,
  row,
  ride,
  disableDuration,
  disableVerticalGain,
  enableDescription,
  enableCertified,
}: {
  disableDuration?: boolean;
  disableVerticalGain?: boolean;
  fullHeight?: boolean;
  enableDescription?: boolean;
  enableCertified?: boolean;
  outlined?: boolean;
  ride?: Ride;
  row?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { toTime, toDistance } = useUnits();

  const partner = ride?.partners?.filter(({ code }) => code !== 'geovelo')?.[0];

  return (
    <Box
      border={outlined ? `1px solid #CFCFCF` : undefined}
      borderRadius={4}
      display="flex"
      flexDirection={{ xs: 'column', md: row ? 'row' : 'column' }}
      gap={outlined ? 0 : 3}
      height={fullHeight ? '100%' : undefined}
      overflow="hidden"
      padding={outlined ? 0 : 3}
      width="100%"
    >
      <Box position="relative" sx={{ aspectRatio: 16 / 9 }} width={{ md: row ? 200 : '100%' }}>
        {ride ? (
          <Box
            borderRadius={outlined ? 0 : 4}
            flexShrink={0}
            height="100%"
            sx={{
              backgroundImage: `url(${ride.photos[0]?.thumbnailUrl || defaultRideImage})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
            width="100%"
          />
        ) : (
          <Skeleton
            height="100%"
            sx={{ borderRadius: outlined ? 0 : 4 }}
            variant="rectangular"
            width="100%"
          />
        )}
        {enableCertified && partner && (
          <Box
            alignItems="center"
            bgcolor="#eef9f4"
            border="1px solid #bfe3d4"
            borderRadius={2}
            bottom={8}
            color="#2ac682"
            display="flex"
            gap={1}
            left={8}
            paddingX={1}
            paddingY={0.5}
            position="absolute"
            zIndex={2}
          >
            <Check color="inherit" fontSize="small" />
            <Typography color="inherit" lineHeight={1} variant="body2">
              {t('geovelo.rides.detail.certified')}
            </Typography>
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="column" flexGrow={1} gap={1} padding={outlined ? 3 : 0}>
        <Box display="flex" flexDirection="column">
          <Typography fontSize="1.125rem" fontWeight={700}>
            {ride ? ride.title : <Skeleton variant="text" width={200} />}
          </Typography>
          {(!ride || ride.from) && (
            <Typography color="textSecondary" variant="body2">
              {ride ? (
                ride.from?.addressDetail && (
                  <>
                    {ride.from.address}
                    {ride.to?.addressDetail &&
                      ride.to.address.localeCompare(ride.from.address) !== 0 && (
                        <>&nbsp;&#x3e;&nbsp;{ride.to.address}</>
                      )}
                  </>
                )
              ) : (
                <Skeleton variant="text" width={150} />
              )}
            </Typography>
          )}
        </Box>
        <Box columnGap={2} display="flex" flexWrap="wrap">
          {!disableDuration && (
            <Box alignItems="center" display="flex" gap={0.5}>
              <DurationIcon color="primary" sx={{ height: 16, width: 16 }} />
              <Typography variant="body2">
                {ride ? (
                  toTime(ride.overloadDuration || ride.duration || 0)
                ) : (
                  <Skeleton variant="text" width={50} />
                )}
              </Typography>
            </Box>
          )}
          <Box alignItems="center" display="flex" gap={0.5}>
            <DistanceIcon color="primary" sx={{ height: 16, width: 16 }} />
            <Typography variant="body2">
              {ride ? toDistance(ride.distance || 0) : <Skeleton variant="text" width={50} />}
            </Typography>
          </Box>
          {!disableVerticalGain && (
            <Box alignItems="center" display="flex" gap={0.5}>
              <ArrowOutward color="primary" sx={{ height: 16, width: 16 }} />
              <Typography variant="body2">
                {ride ? `${ride.verticalGain} m` : <Skeleton variant="text" width={50} />}
              </Typography>
            </Box>
          )}
        </Box>
        {enableDescription && (!ride || ride.description) && (
          <Typography
            color="textSecondary"
            marginTop={1}
            sx={{
              display: '-webkit-box',
              lineHeight: '1.1875em',
              height: '34px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.875em',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
            variant="body2"
          >
            {ride ? (
              ride.description
            ) : (
              <>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="90%" />
              </>
            )}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default RideCard;
